import React, { useEffect, useMemo, useState } from 'react';
import { Btn, DangerBtn, Msg, Section, fmtDate, panelGet, panelPost } from '../kit';
import { inputCls } from '../email/emailKit';
import {
  type ConfigData, type IndustryRow, type Note, Pill, S, SOURCE_LABEL, asStr, first, fmt, num, pct, rowsOf,
} from './shared';

/**
 * Admin → Onboarding → Industries & keywords: the up-to-6 chips each industry
 * offers on step 2.
 *
 * Chips start as the corpus's most common sub-sectors for the industry; an
 * edit here is stored as source "admin:<email>" and survives every corpus
 * rebuild ("Rebuild all from corpus" keeps admin-edited industries; "Reset
 * this industry" is the only way back). Saving re-picks that industry's
 * Cosmos picks lists in the background.
 *
 * Next to each chip: how many technologies match it strictly (the stored
 * picks list's candidates — under 3 means the top 3 is topped up from the
 * industry list) and, from the funnel, how often it is picked and how often
 * picking it ends in Explore.
 */

const MAX_CHIPS = 6;
const MAX_LEN = 60;
const OTHERS = 'Others';

type Suggestion = { keyword: string; count: number | null };
type ChipStat = { picked: number | null; explored: number | null };

const clean = (t: string) => t.replace(/\s+/g, ' ').trim();

const normSuggestions = (d: any): Suggestion[] =>
  rowsOf(d, 'suggestions', 'keywords').map((r: any) => (typeof r === 'string'
    ? { keyword: r, count: null }
    : { keyword: asStr(first(r.keyword, r.sub, r.subcategory, r.name, r.label)), count: num(first(r.count, r.n, r.techs, r.matches)) }))
    .filter(s => s.keyword);

/** industry → keyword → picked / explored, from breakdown {by: industry_keyword}. */
const normChipStats = (d: any): Record<string, Record<string, ChipStat>> => {
  const out: Record<string, Record<string, ChipStat>> = {};
  rowsOf(d, 'breakdown').forEach((r: any) => {
    const ind = asStr(r.industry); const kw = asStr(r.keyword);
    if (!ind || !kw) return;
    (out[ind] = out[ind] || {})[kw] = {
      picked: num(first(r.keyword_picked, r.picked, r.keyword, r.users, r.count)),
      explored: num(first(r.explored, r.explore, r.industry_keyword)),
    };
  });
  return out;
};

const Editor: React.FC<{
  row: IndustryRow; stats: Record<string, ChipStat>; onSaved: (d: any) => void; onDirty: (v: boolean) => void;
  onClose: () => void;
}> = ({ row, stats, onSaved, onDirty, onClose }) => {
  const [chips, setChips] = useState<string[]>(row.chips);
  const [adding, setAdding] = useState('');
  const [sugs, setSugs] = useState<Suggestion[] | null>(null);
  const [sugErr, setSugErr] = useState('');
  const [busy, setBusy] = useState<'' | 'save' | 'reset'>('');
  const [msg, setMsg] = useState<Note>(null);
  const dirty = JSON.stringify(chips) !== JSON.stringify(row.chips);
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);
  useEffect(() => () => onDirty(false), [onDirty]);
  useEffect(() => { if (!dirty) setChips(row.chips); /* fresh server copy */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.chips]);

  useEffect(() => {
    let alive = true;
    panelGet(S, 'keyword_suggestions', { industry: row.name, limit: 20 }).then(({ data, error }) => {
      if (!alive) return;
      if (error) { setSugErr(error); setSugs([]); return; }
      setSugs(normSuggestions(data));
    });
    return () => { alive = false; };
  }, [row.name]);

  const lower = chips.map(c => c.toLowerCase());
  const add = (t: string) => {
    const v = clean(t);
    if (!v) return;
    if (v.length > MAX_LEN) { setMsg({ kind: 'error', text: `A chip is at most ${MAX_LEN} characters.` }); return; }
    if (lower.includes(v.toLowerCase())) { setMsg({ kind: 'error', text: `“${v}” is already a chip.` }); return; }
    if (chips.length >= MAX_CHIPS) { setMsg({ kind: 'error', text: `At most ${MAX_CHIPS} chips — remove one first.` }); return; }
    setMsg(null);
    setChips([...chips, v]);
    setAdding('');
  };
  const moveChip = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= chips.length) return;
    const next = [...chips];
    [next[i], next[j]] = [next[j], next[i]];
    setChips(next);
  };
  const countFor = (c: string): number | null => {
    if (c in row.counts) return row.counts[c];
    const s = sugs?.find(x => x.keyword.toLowerCase() === c.toLowerCase());
    return s ? s.count : null;
  };

  const save = async () => {
    const list = chips.map(clean).filter(Boolean);
    if (!list.length) { setMsg({ kind: 'error', text: 'Keep at least one chip (or hide the industry in Settings).' }); return; }
    setBusy('save'); setMsg(null);
    const { data, error } = await panelPost(S, 'keywords_set', { industry: row.name, keywords: list });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    onSaved(data);
    setMsg({ kind: 'ok', text: 'Saved. Its picks lists are being rebuilt in the background (Cosmos picks tab).' });
  };
  const reset = async () => {
    setBusy('reset'); setMsg(null);
    const { data, error } = await panelPost(S, 'keywords_reset', { industry: row.name });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    onSaved(data);
    setMsg({ kind: 'ok', text: 'Back to the corpus chips for this industry.' });
  };

  const free = (sugs || []).filter(s => !lower.includes(s.keyword.toLowerCase()));

  return (
    <div className="flex flex-col gap-3 border-t border-grayscale-200 pt-3 mt-1">
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="flex flex-col gap-2 min-w-0">
          <div className="text-xs text-grayscale-1100">
            <span className="font-bold text-grayscale-1300">Chips</span> — {chips.length} of {MAX_CHIPS}, in display order (3 per row)
          </div>
          {chips.map((c, i) => {
            const n = countFor(c);
            const st = stats[c];
            return (
              <div key={`${c}-${i}`} className="flex items-center gap-2 rounded-lg border border-grayscale-300 p-2">
                <span className="text-xs text-grayscale-1100 w-5 text-right shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-grayscale-1300 break-words">{c}</div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                    {n !== null && (
                      <Pill tone={n < 3 ? 'warn' : 'grey'} title="Technologies that match this chip strictly (sub-sector) in this industry">
                        {n < 3 ? `only ${n} match${n === 1 ? '' : 'es'}` : `${fmt(n)} matches`}
                      </Pill>
                    )}
                    {st && st.picked !== null && (
                      <span className="text-[11px] text-grayscale-1100 whitespace-nowrap"
                        title="Picked by (people) · of them, went on to Explore">
                        picked {fmt(st.picked)}{st.explored !== null ? ` · explore ${pct(st.explored, st.picked)}` : ''}
                      </span>
                    )}
                  </div>
                </div>
                <span className="flex items-center gap-1 shrink-0">
                  <Btn small onClick={() => moveChip(i, -1)} disabled={i === 0} title="Move up">↑</Btn>
                  <Btn small onClick={() => moveChip(i, 1)} disabled={i === chips.length - 1} title="Move down">↓</Btn>
                  <Btn small kind="danger" onClick={() => setChips(chips.filter((_, j) => j !== i))} title="Remove">×</Btn>
                </span>
              </div>
            );
          })}
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <input value={adding} maxLength={MAX_LEN} placeholder="Type a chip of your own and press Enter"
                onChange={e => setAdding(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(adding); } }}
                className={inputCls} />
            </div>
            <Btn small onClick={() => add(adding)} disabled={!clean(adding) || chips.length >= MAX_CHIPS}>Add</Btn>
          </div>
          <div className="text-[11px] text-grayscale-1100">
            A typed chip matches by text when it is not a sub-sector name, so check its match count after saving.
          </div>
        </div>

        <div className="flex flex-col gap-2 min-w-0">
          <div className="text-xs text-grayscale-1100">
            <span className="font-bold text-grayscale-1300">From the corpus</span> — sub-sectors in {row.name}, most technologies first
          </div>
          {sugs === null ? <div className="text-xs text-grayscale-1100">Loading suggestions…</div>
            : sugErr ? <div className="text-xs text-red-600">Suggestions unavailable: {sugErr}</div>
            : !free.length ? <div className="text-xs text-grayscale-1100">No more suggestions.</div>
            : (
              <div className="flex flex-wrap gap-1.5">
                {free.map(s => (
                  <button key={s.keyword} type="button" onClick={() => add(s.keyword)}
                    disabled={chips.length >= MAX_CHIPS}
                    title={chips.length >= MAX_CHIPS ? `At most ${MAX_CHIPS} chips` : 'Add as a chip'}
                    className="text-xs px-2.5 py-1 rounded-full border border-primary-300 bg-white hover:bg-primary-100 disabled:opacity-50">
                    + {s.keyword}{s.count !== null ? <span className="text-grayscale-1100"> · {fmt(s.count)}</span> : null}
                  </button>
                ))}
              </div>
            )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Btn kind="primary" onClick={save} disabled={!dirty || !!busy}>{busy === 'save' ? 'Saving…' : 'Save chips'}</Btn>
        {dirty && !busy && <Btn onClick={() => { setChips(row.chips); setMsg(null); }}>Discard changes</Btn>}
        {dirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
        <span className="flex-1" />
        {row.source !== 'corpus' && row.source !== 'none' && (busy === 'reset'
          ? <Btn kind="danger" small disabled>Resetting…</Btn>
          : <DangerBtn small onConfirm={reset}>Reset this industry to corpus</DangerBtn>)}
        <Btn small onClick={onClose}>Close</Btn>
      </div>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
    </div>
  );
};

const Keywords: React.FC<{
  data: ConfigData; onSaved: (d: any) => void; onDirty: (v: boolean) => void; reload: () => void;
}> = ({ data, onSaved, onDirty, reload }) => {
  const [open, setOpen] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Note>(null);
  const [stats, setStats] = useState<Record<string, Record<string, ChipStat>>>({});

  useEffect(() => {
    let alive = true;
    panelGet(S, 'breakdown', { by: 'industry_keyword' }).then(({ data: d }) => {
      if (alive && d) setStats(normChipStats(d));
    });
    return () => { alive = false; };
  }, []);

  const rows = useMemo(() => {
    const byName = new Map(data.industries.map(r => [r.name, r]));
    const names = data.all_names.length ? data.all_names : data.industries.map(r => r.name);
    const list = names.map(n => byName.get(n) || {
      name: n, chips: [], source: 'none', by: '', updated_at: null,
      hidden: data.config.hidden_industries.includes(n), counts: {}, candidates: null,
    } as IndustryRow);
    data.industries.forEach(r => { if (!names.includes(r.name)) list.push(r); });
    return [...list.filter(r => r.name !== OTHERS), ...list.filter(r => r.name === OTHERS)];
  }, [data]);

  const rebuildAll = async () => {
    setBusy(true); setMsg(null);
    const { data: next, error } = await panelPost(S, 'keywords_rebuild', { force: false });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    if (next && (next.config || next.industries)) onSaved(next); else reload();
    setMsg({ kind: 'ok', text: 'Rebuilt from the corpus. Admin-edited industries were kept.' });
  };

  const adminCount = rows.filter(r => r.source === 'admin' || r.source === 'default').length;
  const thin = rows.filter(r => r.name !== OTHERS && Object.values(r.counts).some(n => n < 3)).length;

  return (
    <div className="flex flex-col gap-4">
      <Section title="Industries & keywords"
        right={
          <span className="flex items-center gap-2">
            <span className="text-[11px] text-grayscale-1100">
              {adminCount} curated (admin or built-in){thin ? ` · ${thin} with a chip under 3 matches` : ''}
            </span>
            <Btn small onClick={rebuildAll} disabled={busy}
              title="Recompute every industry's chips from the corpus — admin-edited and built-in industries are kept">
              {busy ? 'Rebuilding…' : 'Rebuild all from corpus'}
            </Btn>
          </span>
        }>
        <p className="text-[13px] text-grayscale-1200 max-w-3xl">
          Step 2 of onboarding shows the chips of the chosen industry, three to a row. Saving an industry rebuilds
          its 7 picks lists (6 chips + industry only) in the background. Show / hide and the order live in Settings.
        </p>
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        <div className="flex flex-col gap-2">
          {rows.map(r => {
            const isOthers = r.name === OTHERS;
            const low = Object.values(r.counts).filter(n => n < 3).length;
            return (
              <div key={r.name} className={`rounded-xl border border-grayscale-300 bg-white p-3 ${r.hidden ? 'opacity-60' : ''}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[14rem]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-grayscale-1300">{r.name}</span>
                      {r.hidden && <Pill tone="grey">Hidden</Pill>}
                      {isOthers ? <Pill tone="grey">No chips · 3 random cards</Pill> : (
                        <Pill tone={r.source === 'admin' || r.source === 'default' ? 'tan' : 'grey'}
                          title={r.by ? `Edited by ${r.by}` : undefined}>
                          {SOURCE_LABEL[r.source] || r.source}{r.by ? ` · ${r.by}` : ''}
                        </Pill>
                      )}
                      {low > 0 && <Pill tone="warn">{low} chip{low === 1 ? '' : 's'} under 3 matches</Pill>}
                    </div>
                    {!isOthers && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {r.chips.length ? r.chips.map(c => (
                          <span key={c} className="text-xs px-2.5 py-1 rounded-xl border border-primary-200 bg-white text-grayscale-1300">
                            {c}{c in r.counts ? <span className="text-grayscale-1100"> · {fmt(r.counts[c])}</span> : null}
                          </span>
                        )) : <span className="text-xs text-grayscale-1100">No chips yet — step 2 shows the “no chips” note.</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {r.updated_at && <span className="text-[11px] text-grayscale-1100">{fmtDate(r.updated_at)}</span>}
                    {!isOthers && open !== r.name && <Btn small onClick={() => setOpen(r.name)}>Edit</Btn>}
                  </div>
                </div>
                {open === r.name && !isOthers && (
                  <Editor row={r} stats={stats[r.name] || {}} onSaved={onSaved} onDirty={onDirty}
                    onClose={() => setOpen('')} />
                )}
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
};

export default Keywords;
